import React, { useRef, useState } from 'react'

import { normalizeMetadataUrl, uploadMarketplaceImage } from './marketplaceData'

const MAX_LOGO_SIZE = 1024 * 1024

export default function MarketplaceImageUploader({ value, onChange, onUploadingChange, t }) {
    const inputRef = useRef(null)
    const [uploading, setUploading] = useState(false)
    const [showUrlInput, setShowUrlInput] = useState(false)
    const [url, setUrl] = useState(value || '')
    const [error, setError] = useState('')

    const setUploadState = (next) => {
        setUploading(next)
        onUploadingChange?.(next)
    }

    const selectFile = async (event) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return
        if (!file.type.startsWith('image/')) {
            setError(t('invalidImageType'))
            return
        }
        if (file.size > MAX_LOGO_SIZE) {
            setError(t('logoTooLarge'))
            return
        }

        setError('')
        setUploadState(true)
        try {
            const nextValue = await uploadMarketplaceImage(file)
            setUrl(nextValue)
            onChange(nextValue)
        } catch (uploadError) {
            setError(t('logoUploadFailed'))
        } finally {
            setUploadState(false)
        }
    }

    const applyUrl = () => {
        const nextValue = url.trim()
        if (!nextValue) return
        setError('')
        onChange(nextValue)
        setShowUrlInput(false)
    }

    const remove = () => {
        setUrl('')
        setError('')
        onChange('')
    }

    return <section className="market-logo-uploader">
        <div className="market-logo-uploader__preview">
            <button
                type="button"
                className={`market-logo-uploader__trigger ${value ? 'has-image' : ''}`}
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
            >
                {uploading
                    ? <span className="market-logo-uploader__loading"><i className="fa fa-circle-notch fa-spin" /> {t('uploadingLogo')}</span>
                    : value
                        ? <img src={normalizeMetadataUrl(value)} alt={t('projectLogo')} />
                        : <span><i className="fa fa-cloud-upload-alt" /><strong>{t('uploadMarketLogo')}</strong></span>}
            </button>
            <input ref={inputRef} type="file" accept="image/*" onChange={selectFile} />
        </div>
        <div className="market-logo-uploader__content">
            <span className="market-logo-uploader__label">{t('projectLogo')}</span>
            <p>{t('logoUploadHint')}</p>
            <div className="market-logo-uploader__actions">
                <button type="button" className="market-button is-compact" onClick={() => inputRef.current?.click()} disabled={uploading}>
                    <i className="fa fa-upload" /> {t('uploadMarketLogo')}
                </button>
                <button type="button" className="market-button is-compact" onClick={() => setShowUrlInput((current) => !current)}>
                    <i className="fa fa-link" /> {t('enterRemoteImageUrl')}
                </button>
                {value && <button type="button" className="market-button is-compact is-danger" onClick={remove}>
                    <i className="fa fa-trash-alt" /> {t('removeLogo')}
                </button>}
            </div>
            {showUrlInput && <div className="market-logo-uploader__url">
                <input value={url} onChange={(event) => setUrl(event.target.value)} placeholder={t('remoteImageUrl')} />
                <button type="button" className="market-button is-primary is-compact" onClick={applyUrl} disabled={!url.trim()}>
                    {t('applyImageUrl')}
                </button>
            </div>}
            {error && <p className="market-logo-uploader__error"><i className="fa fa-exclamation-circle" /> {error}</p>}
        </div>
    </section>
}
